import {
  removeUrlHash,
  selectWechatSignatureUrl,
  toAbsoluteWechatUrl,
} from '../utils/wechat-url';
import { api } from './http';

interface JsSdkConfig {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
  openTagList: string[];
}

interface WxApi {
  config(config: JsSdkConfig & { debug: boolean }): void;
  ready(callback: () => void): void;
  error(callback: (error: { errMsg?: string }) => void): void;
  checkJsApi(input: {
    jsApiList: string[];
    success: (result: { checkResult?: Record<string, boolean | string> }) => void;
    fail: (error: { errMsg?: string }) => void;
  }): void;
  previewImage(input: { current: string; urls: string[] }): void;
  scanQRCode(input: {
    needResult: number;
    scanType: string[];
    success: (result: { resultStr: string }) => void;
    fail: (error: unknown) => void;
  }): void;
  getLocation(input: {
    type: string;
    success: (value: GeoLocation) => void;
    fail: (error: unknown) => void;
  }): void;
  openLocation(
    input: GeoLocation & { name?: string; address?: string; scale?: number },
  ): void;
  updateAppMessageShareData?(input: ShareOptions): void;
  updateTimelineShareData?(input: ShareOptions): void;
  onMenuShareAppMessage?(input: ShareOptions): void;
  onMenuShareTimeline?(input: ShareOptions): void;
}

interface GeoLocation {
  latitude: number;
  longitude: number;
}

interface ShareData {
  title: string;
  desc?: string;
  link: string;
  imgUrl?: string;
}

interface ShareOptions extends ShareData {
  success?: () => void;
  fail?: (error: { errMsg?: string }) => void;
}

declare global {
  interface Window {
    wx?: WxApi;
    WeixinJSBridge?: {
      invoke(
        name: string,
        params: Record<string, unknown>,
        callback: (result: { err_msg?: string }) => void,
      ): void;
    };
  }
}

let sdkPromise: Promise<WxApi> | null = null;
let configuredUrl = '';
let configuringUrl = '';
let configurePromise: Promise<WxApi> | null = null;
const initialPageUrl = typeof window === 'undefined' ? '' : window.location.href;

function loadSdk() {
  if (window.wx) return Promise.resolve(window.wx);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
    script.onload = () =>
      window.wx
        ? resolve(window.wx)
        : reject(new Error('微信 JS-SDK 加载失败'));
    script.onerror = () => reject(new Error('微信 JS-SDK 网络加载失败'));
    document.head.append(script);
  });
  return sdkPromise;
}

export const isWechat = () => /micromessenger/i.test(navigator.userAgent);

export async function configureWechat() {
  const signedUrl = selectWechatSignatureUrl({
    initialUrl: initialPageUrl,
    currentUrl: window.location.href,
    userAgent: navigator.userAgent,
  });
  const wx = await loadSdk();
  if (configuredUrl === signedUrl) return wx;
  if (configurePromise && configuringUrl === signedUrl) return configurePromise;

  configuringUrl = signedUrl;
  const pending = (async () => {
    const config = await api.post<JsSdkConfig>(
      '/h5-auth/wechat/js-sdk-signature',
      { url: signedUrl },
      { globalLoading: false },
    );
    await new Promise<void>((resolve, reject) => {
      wx.error((error) => {
        const message = error.errMsg || '微信 JS-SDK 配置失败';
        reject(
          new Error(
            /invalid url domain/i.test(message)
              ? '当前公众号尚未配置该 H5 域名为 JS 接口安全域名'
              : message,
          ),
        );
      });
      wx.config({ debug: false, ...config });
      wx.ready(() => {
        configuredUrl = signedUrl;
        resolve();
      });
    });
    return wx;
  })();

  configurePromise = pending;
  try {
    return await pending;
  } finally {
    if (configurePromise === pending) {
      configurePromise = null;
      configuringUrl = '';
    }
  }
}

export async function previewWechat(urls: string[], current = urls[0] || '') {
  const wx = await configureWechat();
  wx.previewImage({ current, urls });
}

export async function scanCode() {
  const wx = await configureWechat();
  return new Promise<string>((resolve, reject) =>
    wx.scanQRCode({
      needResult: 1,
      scanType: ['qrCode', 'barCode'],
      success: (result) => resolve(result.resultStr),
      fail: reject,
    }),
  );
}

export async function getLocation() {
  const wx = await configureWechat();
  return new Promise<GeoLocation>((resolve, reject) =>
    wx.getLocation({ type: 'gcj02', success: resolve, fail: reject }),
  );
}

export async function openLocation(
  value: GeoLocation & { name?: string; address?: string },
) {
  const wx = await configureWechat();
  wx.openLocation({ ...value, scale: 16 });
}

export async function share(data: ShareData) {
  const wx = await configureWechat();
  const normalized = {
    ...data,
    link: removeUrlHash(
      toAbsoluteWechatUrl(data.link, window.location.origin) ||
        window.location.href,
    ),
    imgUrl: toAbsoluteWechatUrl(data.imgUrl, window.location.origin),
  };
  const update = (method: (input: ShareOptions) => void) =>
    new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
      const timer = window.setTimeout(finish, 1200);
      method({
        ...normalized,
        success: () => {
          window.clearTimeout(timer);
          finish();
        },
        fail: (error) => {
          window.clearTimeout(timer);
          if (!settled) {
            settled = true;
            reject(new Error(error.errMsg || '微信分享卡片配置失败'));
          }
        },
      });
    });

  const updates: Promise<void>[] = [];
  if (wx.updateAppMessageShareData)
    updates.push(update(wx.updateAppMessageShareData.bind(wx)));
  if (wx.updateTimelineShareData)
    updates.push(update(wx.updateTimelineShareData.bind(wx)));
  if (!updates.length) {
    wx.onMenuShareAppMessage?.(normalized);
    wx.onMenuShareTimeline?.(normalized);
  }
  if (
    !updates.length &&
    !wx.onMenuShareAppMessage &&
    !wx.onMenuShareTimeline
  )
    throw new Error('当前微信版本不支持分享卡片，请升级微信后重试');
  await Promise.all(updates);
}

export async function requestMerchantTransfer(params: Record<string, unknown>) {
  const wx = await configureWechat();
  const mchId = String(params.mchId ?? params.mchid ?? '');
  const appId = String(params.appId ?? params.appid ?? '');
  const packageValue = String(params.package ?? params.packageInfo ?? '');
  if (!mchId || !appId || !packageValue) {
    throw new Error('当前记录无需用户确认，到账状态请以后端为准');
  }

  await new Promise<void>((resolve, reject) => {
    wx.checkJsApi({
      jsApiList: ['requestMerchantTransfer'],
      success: (result) => {
        const supported = result.checkResult?.requestMerchantTransfer;
        if (supported === true || supported === 'true') resolve();
        else reject(new Error('当前微信版本暂不支持确认领取，请升级微信后重试'));
      },
      fail: () => reject(new Error('暂时无法检查微信确认领取功能，请稍后重试')),
    });
  });

  return new Promise<void>((resolve, reject) => {
    const invoke = () =>
      window.WeixinJSBridge?.invoke(
        'requestMerchantTransfer',
        { mchId, appId, package: packageValue },
        (result) => {
          const message = result.err_msg || '用户未确认收款';
          if (message.endsWith(':ok')) resolve();
          else if (/function_not_exist|not support/i.test(message))
            reject(new Error('当前微信版本暂不支持确认领取，请升级微信后重试'));
          else reject(new Error(message));
        },
      );
    if (window.WeixinJSBridge) invoke();
    else document.addEventListener('WeixinJSBridgeReady', invoke, { once: true });
  });
}
